import { FieldWrapper } from "@progress/kendo-react-form";
import { RadioGroup, RadioGroupProps } from "@progress/kendo-react-inputs";
import { Error, Hint, Label } from "@progress/kendo-react-labels";
import * as React from "react";
import { useControlledField, type FieldValidator } from "./useControlledField";

export interface FormRadioGroupProps
  extends Omit<RadioGroupProps, "name" | "value" | "onChange" | "valid"> {
  name: string;
  validator?: FieldValidator;
  label?: string;
  hint?: string;
  showErrorOnPristine?: boolean;
  /** Kendo's RadioGroup does not render it, so it goes on the group wrapper. */
  "data-test-id"?: string;
  /**
   * Optional side-effect callback after this field's RHF value is updated by a
   * **user** picking a radio option. Same contract as the `onSelectionChange`
   * on `FormDropDownList` / `FormMultiSelect`: cascade to *other* fields here,
   * never write this one, and never use it for hydration.
   *
   * **Not called** on mount, `reset`, or any programmatic `setValue`.
   */
  onSelectionChange?: (value: unknown) => void;
}

export const FormRadioGroup: React.FC<FormRadioGroupProps> = ({
  name,
  validator,
  label,
  hint,
  showErrorOnPristine = false,
  disabled,
  layout,
  data,
  "data-test-id": testId,
  onSelectionChange,
  ...radioProps
}) => {
  const { field, fieldState } = useControlledField({ name, validator });
  const showError =
    !!fieldState.error && (showErrorOnPristine || fieldState.isTouched);
  const inputId = name;
  const hintId = hint ? `${inputId}_hint` : "";
  const errorId = showError ? `${inputId}_error` : "";
  const labelId = label ? `${inputId}_label` : "";

  return (
    <FieldWrapper>
      {label && (
        <Label
          id={labelId}
          editorId={inputId}
          editorValid={!fieldState.error}
          editorDisabled={!!disabled}
          className="tlx-section__label"
        >
          {label}
        </Label>
      )}
      <div onBlur={field.onBlur} data-test-id={testId}>
        <RadioGroup
          {...radioProps}
          id={inputId}
          name={name}
          ariaDescribedBy={`${hintId} ${errorId}`}
          ariaLabelledBy={labelId}
          valid={!fieldState.error}
          disabled={disabled}
          layout={layout}
          data={data}
          value={field.value}
          onChange={e => {
            field.onChange(e.value);
            onSelectionChange?.(e.value);
          }}
        />
      </div>
      {hint && <Hint id={hintId}>{hint}</Hint>}
      {showError && <Error id={errorId}>{fieldState.error?.message}</Error>}
    </FieldWrapper>
  );
};

export default FormRadioGroup;
